/**
 * Centralized Route Constants and Type-Safe Path Builders
 * 
 * This file provides:
 * - Compile-time route validation
 * - Type-safe path building
 * - Single source of truth for all routes
 * - Prevention of common routing mistakes
 * 
 * CRITICAL: Route parameter names in builder functions MUST match useParams extractions
 * Example: buildCoachPublicRoute({ slug }) → useParams<{ slug: string }>()
 * 
 * Automated tests validate parameter name consistency - see run-automated-tests edge function
 */

// ===========================
// Route Parameter Types
// ===========================

export type BrowseType = 'experiences' | 'coaches';
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

interface ExperienceParams {
  id: string;
}

interface CoachPublicParams {
  slug: string;
}

interface CoachEditParams {
  id?: string; // Optional - if not provided, uses session
}

interface AcademyParams {
  slug: string;
}

interface ManageAcademyParams {
  id: string;
}

interface ExperienceCollaborationParams {
  experienceId: string;
}

interface GroupParams {
  id: string;
}

interface BrowseParams {
  type: BrowseType;
}

// ===========================
// Static Routes (no parameters)
// ===========================

export const ROUTES = {
  // Public routes
  HOME: '/',
  AUTH: '/auth',
  NOT_FOUND: '*',
  
  // User routes
  PROFILE: '/profile',
  DASHBOARD: '/dashboard',
  BOOKINGS: '/bookings',
  BOOKING_SUCCESS: '/booking-success',
  NOTIFICATIONS: '/notifications',
  MESSAGES: '/messages',
  CHAT: '/chat',
  CHECKOUT: '/checkout',
  SKILL_PROGRESSION: '/skill-progression',
  
  // Browse routes (require type parameter)
  BROWSE_BASE: '/browse', // DO NOT USE DIRECTLY - use buildBrowseRoute()
  BROWSE_ACADEMIES: '/browse-academies',
  ALL_EXPERIENCES: '/all-experiences',
  
  // Creation routes
  CREATE_EXPERIENCE: '/create-experience',
  CREATE_ACADEMY: '/create-academy',
  CREATE_GROUP: '/create-group',
  
  // Coach routes
  COACH_EDIT: '/coach-edit', // Preferred edit route (no param, uses session)
  COACH_INVITATIONS: '/coach-invitations',
  
  // Community routes
  LEARNER_GROUPS: '/learner-groups',
  
  // Admin routes
  ADMIN_TEST_SETUP: '/admin/test-setup',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_INVITES: '/admin/invites',
  ADMIN_REPORTS: '/admin/reports',
  
  // Public invite route
  INVITE_LANDING: '/invite/:code',
  
  // Footer/Legal pages
  ABOUT_US: '/about',
  HOW_IT_WORKS: '/how-it-works',
  BECOME_COACH: '/become-coach',
  COACH_ONBOARDING: '/coach-onboarding',
  BECOME_MAESTRO: '/become-maestro',
  BECOME_VENUE_PARTNER: '/partner/venue',
  BECOME_REFERRAL_PARTNER: '/partner/referral',
  AFFILIATE_REGISTER: '/partner/affiliate',
  AFFILIATE_DASHBOARD: '/partner/affiliate/dashboard',
  HELP_CENTER: '/help',
  CONTACT_US: '/contact',
  TERMS_OF_SERVICE: '/terms',
  PRIVACY_POLICY: '/privacy',
  WAIVER_OF_LIABILITY: '/waiver',
  HIGH_RISK_ADDENDUM: '/high-risk-release',
  PROVIDER_AGREEMENT: '/provider-agreement',
  
  // Academy routes
  COACH_ACADEMY: '/coach-academy',
  ACADEMY_APPLY: '/academy/apply',
  ACADEMY_JOIN: '/academy/join',
  
  // Special dashboards
  MAESTRO_COMMAND_CENTER: '/maestro',
  PARTNER_DASHBOARD: '/partner/dashboard',
  COACHES_WANTED: '/coaches-wanted',
  MATEO_OPEN: '/mateo-open',
  MATEO_OPEN_SESSIONS: '/mateo-open/sessions',
  MATEO_STAGE: '/mateo-stage',
  STAGE_ONBOARDING: '/stage-onboarding',
  FOUNDRY_BUILDER: '/foundry/build',
  GIFT_EXPERIENCE: '/gift',
  GIFT_REDEEM: '/gift/redeem',
  BETA_REWARD: '/beta-reward',
  BLOG: '/blog',
} as const;

// ===========================
// Path Builder Functions
// ===========================

/**
 * Build path to experience detail page
 * @example routes.experience({ id: 'abc-123' }) // '/experiences/abc-123'
 */
export function buildExperienceRoute(params: ExperienceParams): string {
  return `/experiences/${params.id}`;
}

/**
 * Build path to coach public profile (VIEW page)
 * Use this for viewing coach profiles, NOT editing
 * @example routes.coachPublic({ slug: 'john-doe' }) // '/coach/public/john-doe'
 */
export function buildCoachPublicRoute(params: CoachPublicParams): string {
  return `/coach/public/${params.slug}`;
}

/**
 * Build path to coach edit page (EDIT page with authorization)
 * WARNING: This route has authorization checks. Use COACH_EDIT for editing own profile.
 * Only use with ID when you need to handle legacy URL params.
 * @example routes.coachEdit({ id: 'user-123' }) // '/coach/user-123'
 */
export function buildCoachEditRoute(params?: CoachEditParams): string {
  if (!params?.id) {
    return ROUTES.COACH_EDIT;
  }
  return `/coach/${params.id}`;
}

/**
 * Build path to academy public profile page
 * @example routes.academy({ slug: 'north-star' }) // '/academy/north-star'
 */
export function buildAcademyRoute(params: AcademyParams): string {
  return `/academy/${params.slug}`;
}

/**
 * Build path to manage academy page
 * @example routes.manageAcademy({ id: 'academy-123' }) // '/manage-academy/academy-123'
 */
export function buildManageAcademyRoute(params: ManageAcademyParams): string {
  return `/manage-academy/${params.id}`;
}

/**
 * Build path to experience collaboration page
 * @example routes.experienceCollaboration({ experienceId: 'exp-123' }) // '/experiences/exp-123/collaborate'
 */
export function buildExperienceCollaborationRoute(params: ExperienceCollaborationParams): string {
  return `/experiences/${params.experienceId}/collaborate`;
}

/**
 * Build path to group detail page
 * @example routes.group({ id: 'group-123' }) // '/groups/group-123'
 */
export function buildGroupRoute(params: GroupParams): string {
  return `/groups/${params.id}`;
}

/**
 * Build path to browse page with required type parameter
 * CRITICAL: Browse page requires type parameter to function correctly
 * @example routes.browse({ type: 'experiences' }) // '/browse?type=experiences'
 * @example routes.browse({ type: 'coaches' }) // '/browse?type=coaches'
 */
export function buildBrowseRoute(params: BrowseParams): string {
  return `/browse?type=${params.type}`;
}

// ===========================
// Convenience Route Builders
// ===========================

/**
 * Quick builders for common routes
 */
export const routes = {
  // Static routes
  home: () => ROUTES.HOME,
  auth: () => ROUTES.AUTH,
  profile: () => ROUTES.PROFILE,
  dashboard: () => ROUTES.DASHBOARD,
  bookings: () => ROUTES.BOOKINGS,
  bookingSuccess: () => ROUTES.BOOKING_SUCCESS,
  notifications: () => ROUTES.NOTIFICATIONS,
  messages: () => ROUTES.MESSAGES,
  chat: () => ROUTES.CHAT,
  checkout: () => ROUTES.CHECKOUT,
  skillProgression: () => ROUTES.SKILL_PROGRESSION,
  browseAcademies: () => ROUTES.BROWSE_ACADEMIES,
  allExperiences: () => ROUTES.ALL_EXPERIENCES,
  createExperience: () => ROUTES.CREATE_EXPERIENCE,
  createAcademy: () => ROUTES.CREATE_ACADEMY,
  createGroup: () => ROUTES.CREATE_GROUP,
  coachEdit: () => ROUTES.COACH_EDIT,
  coachInvitations: () => ROUTES.COACH_INVITATIONS,
  learnerGroups: () => ROUTES.LEARNER_GROUPS,
  adminTestSetup: () => ROUTES.ADMIN_TEST_SETUP,
  adminDashboard: () => ROUTES.ADMIN_DASHBOARD,
  adminInvites: () => ROUTES.ADMIN_INVITES,
  adminReports: () => ROUTES.ADMIN_REPORTS,
  
  // Public invite
  inviteLanding: (code: string) => `/invite/${code}`,
  
  // Footer/Legal pages
  aboutUs: () => ROUTES.ABOUT_US,
  howItWorks: () => ROUTES.HOW_IT_WORKS,
  becomeCoach: () => ROUTES.BECOME_COACH,
  coachOnboarding: () => ROUTES.COACH_ONBOARDING,
  becomeMaestro: () => ROUTES.BECOME_MAESTRO,
  becomeVenuePartner: () => ROUTES.BECOME_VENUE_PARTNER,
  becomeReferralPartner: () => ROUTES.BECOME_REFERRAL_PARTNER,
  affiliateRegister: () => ROUTES.AFFILIATE_REGISTER,
  affiliateDashboard: () => ROUTES.AFFILIATE_DASHBOARD,
  helpCenter: () => ROUTES.HELP_CENTER,
  contactUs: () => ROUTES.CONTACT_US,
  termsOfService: () => ROUTES.TERMS_OF_SERVICE,
  privacyPolicy: () => ROUTES.PRIVACY_POLICY,
  waiverOfLiability: () => ROUTES.WAIVER_OF_LIABILITY,
  highRiskAddendum: () => ROUTES.HIGH_RISK_ADDENDUM,
  providerAgreement: () => ROUTES.PROVIDER_AGREEMENT,
  
  // Academy routes
  coachAcademy: () => ROUTES.COACH_ACADEMY,
  academyApply: () => ROUTES.ACADEMY_APPLY,
  academyJoin: () => ROUTES.ACADEMY_JOIN,
  
  // Special dashboards
  maestroCommandCenter: () => ROUTES.MAESTRO_COMMAND_CENTER,
  partnerDashboard: () => ROUTES.PARTNER_DASHBOARD,
  coachesWanted: () => ROUTES.COACHES_WANTED,
  mateoOpen: () => ROUTES.MATEO_OPEN,
  mateoOpenSessions: () => ROUTES.MATEO_OPEN_SESSIONS,
  mateoStage: () => ROUTES.MATEO_STAGE,
  stageOnboarding: () => ROUTES.STAGE_ONBOARDING,
  foundryBuilder: () => ROUTES.FOUNDRY_BUILDER,
  giftExperience: () => ROUTES.GIFT_EXPERIENCE,
  giftRedeem: () => ROUTES.GIFT_REDEEM,
  betaReward: () => ROUTES.BETA_REWARD,
  blog: () => ROUTES.BLOG,
  
  // Dynamic routes
  experience: (id: string) => buildExperienceRoute({ id }),
  coachPublic: (slug: string) => buildCoachPublicRoute({ slug }),
  coachEditWithId: (id: string) => buildCoachEditRoute({ id }),
  academy: (slug: string) => buildAcademyRoute({ slug }),
  manageAcademy: (id: string) => buildManageAcademyRoute({ id }),
  experienceCollaboration: (experienceId: string) => buildExperienceCollaborationRoute({ experienceId }),
  group: (id: string) => buildGroupRoute({ id }),
  browse: (type: BrowseType) => buildBrowseRoute({ type }),
  
  // Convenience shortcuts
  browseExperiences: () => buildBrowseRoute({ type: 'experiences' }),
  browseCoaches: () => buildBrowseRoute({ type: 'coaches' }),
} as const;

// ===========================
// Deprecated Routes (DO NOT USE)
// ===========================

/**
 * These routes have been removed or replaced.
 * Using them will cause 404 errors or incorrect behavior.
 */
export const DEPRECATED_ROUTES = {
  COACH_PROFILE: '/coach-profile', // Use ROUTES.PROFILE or ROUTES.COACH_EDIT
  SUBSCRIPTIONS: '/subscriptions', // Feature removed, use ROUTES.LEARNER_GROUPS
  EXPERIENCE_SINGULAR: '/experience/:id', // Wrong - use buildExperienceRoute()
  EXPERIENCE_RECURRING: '/experience/:id/recurring', // Feature removed
  BROWSE_WITHOUT_TYPE: '/browse', // Use buildBrowseRoute() with type parameter
} as const;

// ===========================
// Type Exports
// ===========================

export type RouteKey = keyof typeof ROUTES;
export type Route = typeof ROUTES[RouteKey];

// ===========================
// Validation Helpers
// ===========================

/**
 * Validate if a route path matches expected patterns
 */
export function isValidRoute(path: string): boolean {
  const validPatterns = [
    /^\/$/,
    /^\/auth$/,
    /^\/profile$/,
    /^\/dashboard$/,
    /^\/browse\?type=(experiences|coaches)$/,
    /^\/browse-academies$/,
    /^\/experiences\/[^/]+$/,
    /^\/coach-edit$/,
    /^\/coach\/[^/]+$/,
    /^\/coach\/public\/[^/]+$/,
    /^\/academy\/[^/]+$/,
    /^\/create-experience$/,
    /^\/create-academy$/,
    /^\/create-group$/,
    /^\/manage-academy\/[^/]+$/,
    /^\/bookings$/,
    /^\/booking-success$/,
    /^\/messages$/,
    /^\/chat$/,
    /^\/checkout$/,
    /^\/coach-invitations$/,
    /^\/experiences\/[^/]+\/collaborate$/,
    /^\/learner-groups$/,
    /^\/groups\/[^/]+$/,
    /^\/skill-progression$/,
    /^\/admin\/test-setup$/,
    /^\/admin\/dashboard$/,
    /^\/about$/,
    /^\/how-it-works$/,
    /^\/become-coach$/,
    /^\/help$/,
    /^\/contact$/,
    /^\/terms$/,
    /^\/privacy$/,
    /^\/waiver$/,
  ];
  
  return validPatterns.some(pattern => pattern.test(path));
}

/**
 * Check if a route is deprecated
 */
export function isDeprecatedRoute(path: string): boolean {
  return Object.values(DEPRECATED_ROUTES).some(deprecated => 
    path.includes(deprecated.replace(':id', ''))
  );
}

/**
 * Get suggested replacement for deprecated route
 */
export function getRouteReplacement(deprecatedPath: string): string | null {
  if (deprecatedPath.includes('/coach-profile')) {
    return 'Use routes.profile() or routes.coachEdit()';
  }
  if (deprecatedPath.includes('/subscriptions')) {
    return 'Use routes.learnerGroups() - subscription feature removed';
  }
  if (deprecatedPath.match(/\/experience\/[^s]/)) {
    return 'Use routes.experience(id) - must be plural /experiences/';
  }
  if (deprecatedPath.includes('/recurring')) {
    return 'Recurring experiences feature removed';
  }
  if (deprecatedPath === '/browse' && !deprecatedPath.includes('?type=')) {
    return 'Use routes.browse("experiences") or routes.browse("coaches")';
  }
  return null;
}
